import * as THREE from 'three';
import type { CameraPose } from '../types';

/** Same WGS84 coordinate as the NOAA Loader record; not a survey certification. */
export const WALLOPS_HR2 = {
  id: 'wallops_164m_hr2',
  name: 'WAL-HR2',
  lat: 37.945426,
  lon: -75.46209,
  diameterM: 16.4,
  // Place the representative container northeast of the pedestal. Installed
  // equipment orientation and the source model's dimensions are not surveyed.
  headingDeg: -90,
} as const;

export const SITE_FADE_START_M = 140;
export const SITE_FADE_END_M = 600;
export const SITE_MAX_DISTANCE_M = 30_000_000;

export interface GeographicOrigin {
  lat: number;
  lon: number;
  headingDeg: number;
}

export function siteDetailWeight(rangeM: number, extentM = 0): number {
  return 1 - THREE.MathUtils.smoothstep(rangeM, Math.max(SITE_FADE_START_M, extentM * 4), Math.max(SITE_FADE_END_M, extentM * 8));
}

/** Local model axes to east/north/up, in metres. Model +Y is up. */
export function localToEnu(
  local: THREE.Vector3,
  anchor: THREE.Vector3,
  origin: GeographicOrigin = WALLOPS_HR2,
): THREE.Vector3 {
  const yaw = THREE.MathUtils.degToRad(origin.headingDeg);
  const x = local.x - anchor.x,
    z = local.z - anchor.z;
  return new THREE.Vector3(
    Math.cos(yaw) * x + Math.sin(yaw) * z,
    Math.sin(yaw) * x - Math.cos(yaw) * z,
    local.y - anchor.y,
  );
}

/** WGS84 local tangent approximation for campus geometry (<1 km). */
export function sitePoint(
  lon: number,
  lat: number,
  height = 0,
  origin: GeographicOrigin = WALLOPS_HR2,
): THREE.Vector3 {
  const phi = THREE.MathUtils.degToRad(origin.lat);
  const e2 = 6.69437999014e-3;
  const d = 1 - e2 * Math.sin(phi) ** 2;
  const east = ((THREE.MathUtils.degToRad(lon - origin.lon) * 6378137) / Math.sqrt(d)) * Math.cos(phi);
  const north = (THREE.MathUtils.degToRad(lat - origin.lat) * 6378137 * (1 - e2)) / d ** 1.5;
  const yaw = THREE.MathUtils.degToRad(origin.headingDeg);
  return new THREE.Vector3(
    Math.cos(yaw) * east + Math.sin(yaw) * north,
    height,
    Math.sin(yaw) * east - Math.cos(yaw) * north,
  );
}

/** Logarithmic travel keeps metre-to-globe flights smooth across seven orders
 * of magnitude; linear position interpolation jumps out of the close scene. */
export function siteFlightPose(from: CameraPose, to: CameraPose, t: number): CameraPose {
  const a = new THREE.Vector3(...from.target),
    b = new THREE.Vector3(...to.target);
  // Wide viewing targets move towards Earth's centre. Interpolate camera
  // positions above the site, never through the planet when returning home.
  if (a.y < -100000) a.y = 0;
  if (b.y < -100000) b.y = 0;
  const da = new THREE.Vector3(...from.position).sub(a),
    db = new THREE.Vector3(...to.position).sub(b);
  const radius = Math.exp(
    THREE.MathUtils.lerp(Math.log(Math.max(0.1, da.length())), Math.log(Math.max(0.1, db.length())), t),
  );
  const direction = da.normalize().lerp(db.normalize(), t).normalize();
  const target = a.lerp(b, t);
  return {
    target: target.toArray(),
    position: target.clone().addScaledVector(direction, radius).toArray(),
    fov: THREE.MathUtils.lerp(from.fov ?? 45, to.fov ?? 45, t),
  };
}
